/**
 * ------------------------ INPUT MODEL PARSING ERROR  -------------------------
 */

import type { Input } from "../../../../api/call";
import type { Result } from "../../../../lib/patterns/result";
import { Err, Ok, isResultErr } from "../../../../lib/patterns/result";
import { hasTimeFunctionDependency } from "diagnostics-lib/runtime";
import type { FILE_PARSING_ERROR } from "../../common/file";
import { parseFile } from "../../common/file";
import type { Parser, ParsingError, ParsingErrorMessageMap, ParsingResult } from "../../types";
import {
  columns,
  columnsMandatory,
  hasDuplicatedRowByQuantityId,
  hasInvalidAscendingOrderExternalGap,
  hasInvalidColumns,
  hasInvalidDefinitionExternalGap,
  hasInvalidFileEmpty,
  hasInvalidInputModelNameSize,
  hasInvalidOrderExternalGapValue,
  hasInvalidReferenceValue,
  hasNoValueForQuantityName,
  hasUnexpectedQuantityName,
} from "./rules";

/**
 * @description Data that can be parsed from an observable file.
 */
export type InputModelParsingResultData = Input<
  "POST",
  "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/inputs"
>["body"]["inputs"];
export type InputModelParsingResult = ParsingResult<InputModelParsingResultData>;
/**
 * @description Error that can be thrown by the input model parser.
 */
export enum INPUT_MODEL_PARSING_ERROR {
  FILE_INVALID_CONTENT = "FILE_INVALID_CONTENT",
  FILE_EMPTY = "FILE_EMPTY",
  DATA_FILE_EMPTY = "DATA_FILE_EMPTY",
  DATA_QUANTITY_DUPLICATED = "DATA_QUANTITY_DUPLICATED",
  DATA_QUANTITY_NAME_EMPTY = "DATA_QUANTITY_NAME_EMPTY",
  DATA_QUANTITY_NAME_NOT_ALLOWED = "DATA_QUANTITY_NAME_NOT_ALLOWED",
  DATA_ORDER_EXTERNAL_GAP_INVALID = "DATA_ORDER_EXTERNAL_GAP_INVALID",
  DATA_REFERENCE_VALUE_INVALID = "DATA_REFERENCE_VALUE_INVALID",
  DATA_EXTERNAL_GAP_INVALID = "DATA_EXTERNAL_GAP_INVALID",
  DATA_ORDER_EXTERNAL_GAP_NOT_ASCENDING = "DATA_ORDER_EXTERNAL_GAP_NOT_ASCENDING",
  DATA_INPUT_MODEL_NAME_TOO_LONG = "DATA_INPUT_MODEL_NAME_TOO_LONG",
  HAS_TIME_FUNCTION_DEPENDENCY = "HAS_TIME_FUNCTION_DEPENDENCY",
}
export type InputModelParsingErrorMessageMap = ParsingErrorMessageMap<
  INPUT_MODEL_PARSING_ERROR | FILE_PARSING_ERROR
>;
export type InputModelParsingError = ParsingError<INPUT_MODEL_PARSING_ERROR | FILE_PARSING_ERROR>;

/**
 * @description Parse input model file content.
 * @param file Input model file to parse
 * @returns
 */
export const parse: Parser<
  InputModelParsingResult,
  InputModelParsingError[],
  {
    expectedQuantityNameValues: string[];
    quantityIdByName: Record<string, string>;
    expressionEngineInput: {
      corpus: Record<string, string | null>;
      graph: {
        [token: string]: string[] | null;
      } | null;
    };
  }
> = async (file, { expectedQuantityNameValues, quantityIdByName, expressionEngineInput }) => {
  /**
   * @description Check for common file issue.
   */
  const errors: InputModelParsingError[] = [];
  const fileResult = await parseFile(file, {
    expectedMimeType: ["text/csv", "application/vnd.ms-excel"],
  });

  /** Have to stop here, to handle file error */
  if (isResultErr(fileResult)) {
    errors.push(fileResult.value);
    return Err(errors);
  }

  /**
   * Get file content.
   */
  const content = fileResult.value as string;

  /**
   * Formatted file content.
   */
  const [header, ...data] = content.split("\n").map(line =>
    line
      .replace(/\r/, "")
      .split(";")
      .map(v => v.replaceAll('"', "")),
  );

  /**
   * Format the data to be processed
   */
  const indiceByColumnNameData = columns.reduce<{ [key: string]: number }>((obj, cols) => {
    const indexColumnData = header ? header.findIndex(col => cols.includes(col)) : -1;
    return {
      ...obj,
      ...cols.reduce((acc, columnName) => ({ ...acc, [columnName]: indexColumnData }), {}),
    };
  }, {});
  /**
   * We focus on a single column naming without taking into account the different versions between Lithium and Helium.
   */
  const columnHeaderNormalizes = columns.flatMap(([col]) => col ?? []);
  const targetData = data.map(line =>
    columnHeaderNormalizes.reduce<{ [key: string]: string | undefined }>(
      (acc, columnName) => ({
        ...acc,
        [columnName]: line[indiceByColumnNameData[columnName] ?? -1],
      }),
      {},
    ),
  );

  /**
   * Check Format
   */
  if (hasInvalidColumns(header)) {
    errors.push({
      kind: INPUT_MODEL_PARSING_ERROR.FILE_INVALID_CONTENT,
      explain: "file don't contains expected columns",
      location: { file: file.name, line: 0 },
      data: {
        hint: columnsMandatory.join(", "),
        value: header?.join(", "),
      },
    });
  }

  /**
   * Filters model input data by excluding model output data.
   */
  const targetDataForInputModel = targetData.filter(
    line => line["inputModelName"] && line["inputModelName"].trim().length > 0,
  );

  /**
   * Check global data.
   */
  const quantityIds = targetDataForInputModel
    .map(({ name: quantityId } = {}) => quantityId)
    .filter(Boolean);
  if (hasDuplicatedRowByQuantityId(quantityIds))
    errors.push({
      kind: INPUT_MODEL_PARSING_ERROR.DATA_QUANTITY_DUPLICATED,
      explain: "file contain duplicated row",
      location: { file: file.name, line: 0 },
      data: {
        key: "name",
        value: quantityIds.find((quantityId, index) => quantityIds.indexOf(quantityId) !== index),
      },
    });

  const orderExternalGapValues = targetDataForInputModel.flatMap(line =>
    line["orderExternalGap"] ? Number(line["orderExternalGap"]) : [],
  );
  if (hasInvalidAscendingOrderExternalGap(orderExternalGapValues)) {
    errors.push({
      kind: INPUT_MODEL_PARSING_ERROR.DATA_ORDER_EXTERNAL_GAP_NOT_ASCENDING,
      explain: "'orderExternalGap' values must be in ascending order",
      location: { file: file.name, line: 0 },
      data: {
        key: "orderExternalGap",
        value: orderExternalGapValues,
      },
    });
  }
  if (hasInvalidFileEmpty(targetDataForInputModel))
    errors.push({
      kind: INPUT_MODEL_PARSING_ERROR.DATA_FILE_EMPTY,
      explain: "empty data file",
      location: { file: file.name, line: 0 },
      data: {
        value: data.length,
      },
    });

  /**
   * @description Check by line data.
   * @see {parseData}
   */
  const dataResult = parseData(
    file,
    targetDataForInputModel,
    expectedQuantityNameValues,
    expressionEngineInput,
  );
  if (isResultErr(dataResult)) errors.push(...dataResult.value);

  /** if any errors, return them, otherwise return parser result */
  return errors.length > 0
    ? Err(errors)
    : Ok({
        version: header?.[0] === "name" ? "lithium" : "helium",
        data: (dataResult.value as InputModelParsingResultData).map(v => ({
          ...v,
          quantityId: quantityIdByName[v.quantityId] as string,
        })),
      });
};

interface InputModel {
  quantityId: string;
  modelName: string;
  externalGap?: {
    order: number;
    reference: number;
  };
}

const parseData = (
  file: File,
  data: {
    [key: string]: string | undefined;
  }[],
  expectedNameValues: string[],
  expressionEngineInput: {
    corpus: Record<string, string | null>;
    graph: {
      [token: string]: string[] | null;
    } | null;
  },
): Result<InputModelParsingResultData, InputModelParsingError[]> => {
  const errors: InputModelParsingError[] = [];
  const inputModels: InputModel[] = [];

  for (let line = 0; line < data.length; line++) {
    const {
      name: quantityId = "",
      inputModelName: modelName = "",
      orderExternalGap = "",
      referenceValue: reference = "",
    } = data[line] || {};

    if (
      expressionEngineInput.graph &&
      hasTimeFunctionDependency(
        [expressionEngineInput.corpus[quantityId] ?? ""],
        expressionEngineInput.corpus,
        expressionEngineInput.graph,
      )
    )
      errors.push({
        kind: INPUT_MODEL_PARSING_ERROR.HAS_TIME_FUNCTION_DEPENDENCY,
        explain: 'invalid "past_value" call, cannot use another time formula as a parameter',
        location: { file: file.name, line: line + 1 },
        data: {
          key: "inputModelName",
          value: modelName,
          hint: expectedNameValues.join(", "),
        },
      });

    if (hasInvalidInputModelNameSize(modelName))
      errors.push({
        kind: INPUT_MODEL_PARSING_ERROR.DATA_INPUT_MODEL_NAME_TOO_LONG,
        explain: "'inputModelName' or 'boundaryConditionInputModelName' is too long",
        location: { file: file.name, line },
        data: {
          key: "inputModelName",
          value: quantityId,
          hint: expectedNameValues.join(", "),
        },
      });

    if (hasNoValueForQuantityName(quantityId))
      errors.push({
        kind: INPUT_MODEL_PARSING_ERROR.DATA_QUANTITY_NAME_EMPTY,
        explain: "file missing value for column 'name' or 'technicalName'",
        location: { file: file.name, line },
        data: {
          key: "name",
          value: quantityId,
          hint: expectedNameValues.join(", "),
        },
      });

    if (hasUnexpectedQuantityName(quantityId, expectedNameValues))
      errors.push({
        kind: INPUT_MODEL_PARSING_ERROR.DATA_QUANTITY_NAME_NOT_ALLOWED,
        explain: "file contains unexpected value for column 'name' or 'technicalName'",
        location: { file: file.name, line },
        data: {
          key: "name",
          hint: expectedNameValues.join(", "),
          value: quantityId,
        },
      });

    if (hasInvalidOrderExternalGapValue(orderExternalGap)) {
      errors.push({
        kind: INPUT_MODEL_PARSING_ERROR.DATA_ORDER_EXTERNAL_GAP_INVALID,
        explain: "value for column 'orderExternalGap' must be a integer greater than or equal to 1",
        location: { file: file.name, line },
        data: {
          key: "orderExternalGap",
          hint: orderExternalGap,
          value: quantityId,
        },
      });
    }

    if (hasInvalidReferenceValue(reference)) {
      errors.push({
        kind: INPUT_MODEL_PARSING_ERROR.DATA_REFERENCE_VALUE_INVALID,
        explain: "value for column 'referenceValue' must be a number",
        location: { file: file.name, line },
        data: {
          key: "referenceValue",
          hint: reference,
          value: quantityId,
        },
      });
    }

    if (hasInvalidDefinitionExternalGap(orderExternalGap, reference)) {
      errors.push({
        kind: INPUT_MODEL_PARSING_ERROR.DATA_EXTERNAL_GAP_INVALID,
        explain:
          "value for column 'referenceValue' must be a defined if value for 'orderExternalGap' is defined",
        location: { file: file.name, line },
        data: {
          key: "orderExternalGap",
          hint: "orderExternalGap - " + orderExternalGap + ",reference - " + reference,
          value: quantityId,
        },
      });
    }

    /**
     * Everything is fine, we can add the input model to the list.
     */
    inputModels.push({
      quantityId,
      modelName,
      externalGap:
        orderExternalGap && reference
          ? { order: parseInt(orderExternalGap), reference: parseFloat(reference) }
          : undefined,
    });
  }

  return errors.length > 0 ? Err(errors) : Ok(inputModels);
};
